/**
 * The public opt-out call behind the invite footer's "Never contact me again" link (WP-B6).
 *
 * Deliberately NOT on the `RefereeApiClient` seam. Every verb there takes a referee
 * `selector.verifier` token and speaks for a specific reference request; this one is
 * unauthenticated, carries a different token entirely, and has nothing to do with a session — a
 * suppression link keeps working long after every request it was minted alongside is closed.
 *
 * ## Why the token goes in a POST body and not a GET
 * Suppression is a state change, and mail clients, link scanners and safe-link proxies GET the URLs
 * in an email without a human ever clicking. So the emailed link lands on a PAGE (routes/OptOut.tsx)
 * whose button POSTs here — see `SuppressionEndpoints`, which is explicit that this is the reason.
 * Nothing in this module may be called on mount.
 *
 * ## What the caller can and cannot learn
 * The backend answers a uniform 200 for ANY valid token, whether or not a row was written, precisely
 * so it is not an oracle for which addresses are already suppressed. That is why
 * {@link SuppressionOutcome} has no `already_suppressed` member: the information does not exist on
 * the wire, and a page that claimed to know would be inventing it. Only a malformed/forged/mis-keyed
 * token is a 400.
 *
 * ## The token is PII
 * The link token is `base64url(payload).base64url(hmac)` over `e:<address>` — the address is
 * RECOVERABLE from it by anyone holding the link (acceptable: the link only ever goes to that same
 * address). Two consequences honoured here: the failure path never logs or re-throws anything
 * carrying the request URL, and `lib/sentry.ts` scrubs `token=` out of every URL before send.
 * We also never DECODE it to show the address — the signature cannot be checked in the browser, so a
 * forged link could be made to display any address at all.
 */
import type { components, paths } from './api-types';

/** Which of the two opt-out surfaces this click came from. They differ only in `source_detail`. */
export type SuppressionSurface = 'never-contact' | 'unsubscribe';

/**
 * Everything this call can tell us.
 *  - `suppressed` — the address is on the do-not-contact list. Covers the idempotent repeat: a
 *    second click on the same link is the same 200, and must read the same to the referee.
 *  - `invalid_link` — a 400. The token was malformed, truncated (email clients break long links),
 *    forged, or signed under a rotated key. NOT retryable, and NOT "expired": these tokens have no
 *    expiry by design, so nothing here may tell the referee to wait or come back later.
 *  - `unavailable` — network failure or a 5xx. The only retryable outcome.
 */
export type SuppressionOutcome = 'suppressed' | 'invalid_link' | 'unavailable';

/**
 * The two endpoint paths, checked against the generated OpenAPI document at COMPILE time.
 *
 * `satisfies Record<…, keyof paths>` is the point of this constant: if either route is renamed or
 * dropped from the API, this stops the build instead of shipping a footer link that resolves to a
 * page that POSTs into the void. That is the exact failure this whole surface exists to fix, so it
 * is worth pinning rather than trusting a string.
 */
const SURFACE_PATH = {
  'never-contact': '/api/public/never-contact',
  unsubscribe: '/api/public/unsubscribe',
} as const satisfies Record<SuppressionSurface, keyof paths>;

/** Same resolution as `api-client.http.ts`: blank `VITE_API_BASE_URL` means same-origin. */
const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

/**
 * Dev/demo fixture tokens, mirroring the reserved-token convention in `api-client.mock.ts` so every
 * state of the page is reachable without a backend (open `/never-contact?token=broken`).
 */
const MOCK_INVALID_TOKEN = 'broken';
const MOCK_UNAVAILABLE_TOKEN = 'offline';

/**
 * Which backing to use — the same env switch, with the same defaults, as
 * `api-client.ts`'s `shouldUseMocks()`. Deliberately a local copy rather than an import: that module
 * instantiates the whole referee session client as a side effect of being loaded, and this surface
 * resolves no session and needs none of it.
 *
 * Named `shouldUseMocks`, not `useMocks`, for the same reason the api-client's is: eslint's
 * react-hooks plugin treats any `use…()` call as a hook and rejects it outside a component.
 */
function shouldUseMocks(): boolean {
  const flag = import.meta.env.VITE_USE_MOCKS;
  if (flag === 'true') return true;
  if (flag === 'false') return false;
  return import.meta.env.DEV;
}

/**
 * Add the address carried by `token` to the global do-not-contact list.
 *
 * Never throws: every failure is one of the {@link SuppressionOutcome} values, because the caller's
 * job is to choose a sentence, not to handle an exception. A blank token is answered locally without
 * a request — there is nothing to send and the endpoint would only 400.
 */
export async function suppress(surface: SuppressionSurface, token: string): Promise<SuppressionOutcome> {
  if (!token.trim()) return 'invalid_link';
  if (shouldUseMocks()) return mockSuppress(token);

  const body: components['schemas']['SuppressionLinkBody'] = { token };

  let response: Response;
  try {
    response = await fetch(`${BASE}${SURFACE_PATH[surface]}`, {
      method: 'POST',
      // `credentials: 'omit'` on purpose. The token authorizes the write and nothing else should:
      // in production the API is same-origin, so the default ('same-origin') would attach the
      // referee's elevated session cookie to a public endpoint that has no business seeing it.
      credentials: 'omit',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    // Swallowed, not re-thrown or logged: a fetch failure carries the request URL, and this URL
    // carries the token, from which the referee's email address is recoverable.
    return 'unavailable';
  }

  if (response.ok) return 'suppressed';
  // The endpoint 400s only on token validation, so a 400 is always "this link cannot be read".
  return response.status === 400 ? 'invalid_link' : 'unavailable';
}

function mockSuppress(token: string): SuppressionOutcome {
  if (token === MOCK_INVALID_TOKEN) return 'invalid_link';
  if (token === MOCK_UNAVAILABLE_TOKEN) return 'unavailable';
  return 'suppressed';
}
